/**
 * CitationDrawer - modal-like overlay showing the full chunk text plus
 * provenance (PROD-590).
 *
 * Opens when the clinician clicks "open" on a `<CitationStrip>` row.
 * Shows the full chunk text plus structured provenance: source citation,
 * section, retrieval timestamp, tier, and the source URL when present.
 *
 * Pattern: same hand-rolled portal-free dialog shell used by
 * `OverrideModal`. The trap is `useFocusTrap` from `./FocusTrap`. ESC
 * dismisses; clicking the backdrop dismisses; focus restores to the
 * trigger that opened the drawer.
 *
 * HC3 citation discipline: the drawer is the rendering surface for HC8
 * corpus-as-floor evidence. It displays the chunk verbatim, no summary
 * + no rewrite; the only transformation is whitespace normalization for
 * visible layout.
 *
 * HC2: Tailwind classes resolve through Signal DS tokens (no raw hex).
 * HC5: zero em / en dashes anywhere in rendered copy or comments.
 *
 * HC6 a11y:
 *   - `role="dialog"` + `aria-modal="true"` on the shell
 *   - `aria-labelledby` -> the canonical title node (the source citation)
 *   - `aria-describedby` -> the chunk text region
 *   - Focus trap captures Tab + Shift+Tab inside the shell
 *   - ESC dismisses (`onClose` prop)
 *   - First interactive element auto-focuses (the close button)
 *   - Focus returns to the trigger when the drawer closes (FocusTrap)
 */
import { useId, useRef } from 'react';
import { useFocusTrap } from './FocusTrap';
import type { RetrievedChunk } from '../services/corpus-retrieval';

export interface CitationDrawerProps {
  /** Controlled-open prop. Caller owns the lifecycle. */
  open: boolean;
  /** Called on Cancel button, ESC, or backdrop click. */
  onClose: () => void;
  /**
   * The chunk to display. May be `null` in the brief moment between
   * close + unmount; the component renders nothing in that case.
   */
  chunk: RetrievedChunk | null;
}

const DRAWER_TITLE_PREFIX = 'Citation';
const CLOSE_LABEL = 'Close';
const SECTION_LABEL = 'Section';
const TIER_LABEL = 'Tier';
const RETRIEVED_LABEL = 'Retrieved';
const SOURCE_LABEL = 'Source';
const CHUNK_TEXT_LABEL = 'Excerpt';

/**
 * Format an ISO timestamp for the drawer's "Retrieved" line. Falls back
 * to the raw string if `Date` cannot parse the input.
 */
export function formatRetrievedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}

export function CitationDrawer({ open, onClose, chunk }: CitationDrawerProps) {
  const titleId = useId();
  const descriptionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useFocusTrap(containerRef, { enabled: open && chunk !== null, onEscape: onClose });

  if (!open || !chunk) return null;

  return (
    <div
      data-testid="citation-drawer-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary/60 px-gutter-md py-gutter-md"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-testid="citation-drawer"
        data-chunk-id={chunk.chunk_id}
        tabIndex={-1}
        className="flex w-full max-w-2xl flex-col gap-gutter-md rounded-signal-lg bg-surface-base shadow-signal-elevated ring-1 ring-surface-border"
      >
        <header className="flex items-start justify-between gap-gutter-md border-b border-surface-border px-gutter-lg py-gutter-md">
          <div className="flex flex-col gap-1">
            <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
              {DRAWER_TITLE_PREFIX}
            </span>
            <h2
              id={titleId}
              data-testid="citation-drawer-title"
              className="text-signal-lg font-semibold text-text-primary"
            >
              {chunk.source_citation}
            </h2>
          </div>
          <button
            type="button"
            data-testid="citation-drawer-close-button"
            onClick={onClose}
            className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-1 text-signal-sm font-semibold text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
          >
            {CLOSE_LABEL}
          </button>
        </header>

        <dl
          data-testid="citation-drawer-provenance"
          className="grid grid-cols-2 gap-gutter-sm px-gutter-lg text-signal-sm text-text-secondary"
        >
          {chunk.section ? (
            <>
              <dt className="font-semibold text-text-tertiary">{SECTION_LABEL}</dt>
              <dd
                data-testid="citation-drawer-section"
                className="text-text-primary"
              >
                {chunk.section}
              </dd>
            </>
          ) : null}
          <dt className="font-semibold text-text-tertiary">{TIER_LABEL}</dt>
          <dd data-testid="citation-drawer-tier" className="text-text-primary">
            {chunk.tier}
          </dd>
          <dt className="font-semibold text-text-tertiary">{RETRIEVED_LABEL}</dt>
          <dd
            data-testid="citation-drawer-retrieved-at"
            className="text-text-primary"
          >
            {formatRetrievedAt(chunk.retrieved_at)}
          </dd>
          <dt className="font-semibold text-text-tertiary">{SOURCE_LABEL}</dt>
          <dd
            data-testid="citation-drawer-source"
            className="break-all text-text-primary"
          >
            {chunk.source_id.startsWith('http') ? (
              <a
                href={chunk.source_id}
                target="_blank"
                rel="noreferrer noopener"
                className="underline hover:text-text-secondary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
              >
                {chunk.source_id}
              </a>
            ) : (
              <span>{chunk.source_id}</span>
            )}
          </dd>
        </dl>

        <section
          data-testid="citation-drawer-text"
          aria-label={CHUNK_TEXT_LABEL}
          id={descriptionId}
          className="flex flex-col gap-1 border-t border-surface-border px-gutter-lg py-gutter-md"
        >
          <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {CHUNK_TEXT_LABEL}
          </span>
          <p className="whitespace-pre-wrap text-signal-sm leading-relaxed text-text-primary">
            {chunk.text}
          </p>
        </section>
      </div>
    </div>
  );
}

export {
  DRAWER_TITLE_PREFIX,
  CLOSE_LABEL,
  SECTION_LABEL,
  TIER_LABEL,
  RETRIEVED_LABEL,
  SOURCE_LABEL,
  CHUNK_TEXT_LABEL,
};
